import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { addDays, format } from "date-fns";
import DatePicker from "react-datepicker";
import { doc, getDocFromServer } from "firebase/firestore";
import { db } from "../../../firebase";
import IncomingDateInput from "./IncomingDateInput";
import { formatIncomingDate, parseIncomingDate } from "./incomingPatientDates";
import { getIncomingBookingPolicy, incomingBookingDay, isIncomingBookingDayAllowed } from "./incomingBookingAvailability";
import { fetchIncomingAvailability } from "./incomingBookingAvailabilityQuery";

const monthKey = (date) => format(date, "yyyy-MM");

const IncomingStudyDateInput = forwardRef(function IncomingStudyDateInput({ nodeId, customer, bookingLink, defaultValue, onChange, ...props }, ref) {
  const policy = getIncomingBookingPolicy(customer);
  const enabled = Boolean(nodeId && policy);
  const today = incomingBookingDay(new Date(Date.now()));
  const calendarDate = (value) => parseIncomingDate(value && value >= today ? value : today);
  const [selected, setSelected] = useState(defaultValue || "");
  const [month, setMonth] = useState(() => monthKey(calendarDate(defaultValue)));
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const [dayCheck, setDayCheck] = useState(null);
  const [linkedBooking, setLinkedBooking] = useState(null);
  const scope = `${nodeId}|${customer}|${month}|${refresh}|${today}`;
  const current = result && result.scope === scope ? result : null;
  const bookingPath = bookingLink && bookingLink.path;
  const linked = linkedBooking && linkedBooking.path === bookingPath ? linkedBooking : null;
  const locked = Boolean(bookingPath && (!linked || linked.error));

  useEffect(() => {
    setSelected(defaultValue || "");
  }, [defaultValue]);

  useEffect(() => {
    if (!bookingPath) return undefined;
    let active = true;
    getDocFromServer(doc(db, bookingPath)).then((snapshot) => {
      // A reservation may have been replaced in Booking Calendar. The server
      // resolves the latest patient booking when saving; a missing old document
      // must not prevent the operator from selecting that date.
      if (active) setLinkedBooking({ path: bookingPath, missing: !snapshot.exists() });
    }).catch(() => { if (active) setLinkedBooking({ path: bookingPath, error: true }); });
    return () => { active = false; };
  }, [bookingPath, refresh]);

  useImperativeHandle(ref, () => ({
    checkAvailability: async (day) => {
      const date = parseIncomingDate(day);
      if (!enabled || !date || day < today) throw new Error("Select today or a future booking date.");
      if (!isIncomingBookingDayAllowed(day, customer)) {
        throw Object.assign(new Error("CHG11 รับจองเฉพาะวันพฤหัสบดี ศุกร์ และเสาร์"), { details: { reason: "booking-day-not-allowed" } });
      }
      setDayCheck({ scope, day, checking: true });
      try {
        const days = await fetchIncomingAvailability(nodeId, day, format(addDays(date, 1), "yyyy-MM-dd"), customer);
        const info = days[day] || { total: 0, occupied: 0, available: 0 };
        setDayCheck({ scope, day, info });
        return info;
      } catch (error) {
        setDayCheck({ scope, day, error: true });
        throw error;
      }
    },
  }), [enabled, nodeId, customer, scope, today]);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    const [year, monthNumber] = month.split("-").map(Number);
    // Include adjacent months so every visible day in the picker is covered.
    const first = [today, format(new Date(year, monthNumber - 2, 1), "yyyy-MM-dd")].sort().pop();
    const last = format(new Date(year, monthNumber + 1, 1), "yyyy-MM-dd");
    fetchIncomingAvailability(nodeId, first, last, customer).then((days) => {
      if (!active) return;
      setResult({ scope, first, last, days });
    }).catch(() => {
      if (active) setResult({ scope, error: true });
    });
    return () => { active = false; };
  }, [enabled, nodeId, customer, month, refresh, scope, today]);

  const availability = (day) => {
    if (day < today) return null;
    if (dayCheck && dayCheck.scope === scope && dayCheck.day === day) return dayCheck.info || null;
    if (!current || current.error || day < current.first || day >= current.last) return null;
    return current.days[day] || { total: 0, occupied: 0, available: 0 };
  };
  const selectedDay = availability(selected);
  const selectedCheck = dayCheck && dayCheck.scope === scope && dayCheck.day === selected ? dayCheck : null;
  const checking = selectedCheck ? selectedCheck.checking : !current;
  const failed = selectedCheck ? selectedCheck.error : current && current.error;
  const chooseDate = (event) => {
    if (props.disabled || locked) return;
    if (enabled && event.target.value && (event.target.value < today || !isIncomingBookingDayAllowed(event.target.value, customer))) return;
    setSelected(event.target.value);
    if (event.target.value) setMonth(event.target.value.slice(0, 7));
    onChange(event);
  };
  const dayTone = (date) => {
    if (format(date, "yyyy-MM-dd") < today) return "past";
    if (!isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)) return "empty";
    const info = availability(format(date, "yyyy-MM-dd"));
    return !info ? "unknown" : !info.total ? "empty" : !info.available ? "full" : info.available === 1 ? "limited" : "open";
  };
  const refreshAvailability = () => setRefresh((value) => value + 1);
  return (
    <div className={enabled ? "incoming-availability" : undefined}>
      {enabled && <div className="incoming-availability-toolbar">
        <div><strong>เลือกวันที่มีห้องว่าง</strong><div className="small text-muted">{policy.label} · โควตา Incoming สูงสุด {policy.maxDaily} คน/วัน{policy.weekdays && " · พฤหัสบดี ศุกร์ เสาร์"}</div></div>
        <button type="button" className="btn btn-outline-primary btn-sm" disabled={props.disabled || !current} onClick={refreshAvailability}>อัปเดตห้องว่าง</button>
      </div>}
      {enabled && <div className="incoming-availability-legend" aria-label="คำอธิบายสถานะห้องว่าง">
        <span className="incoming-legend-open">ว่าง 2–{policy.maxDaily} ที่</span>
        <span className="incoming-legend-limited">เหลือ 1 ที่</span>
        <span className="incoming-legend-full">เต็ม</span>
        <span className="incoming-legend-empty">ไม่มีรอบจอง</span>
      </div>}
      <div className={enabled ? "incoming-availability-layout" : undefined}>
      {enabled && <DatePicker
        inline
        minDate={parseIncomingDate(today)}
        filterDate={(date) => isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)}
        selected={parseIncomingDate(selected)}
        openToDate={calendarDate(selected)}
        onChange={(date) => chooseDate({ target: { name: props.name, value: date ? format(date, "yyyy-MM-dd") : "" } })}
        onMonthChange={(date) => setMonth(monthKey(date))}
        onYearChange={(date) => setMonth(monthKey(date))}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        disabled={props.disabled || locked}
        calendarClassName="incoming-availability-calendar"
        dayClassName={(date) => `incoming-availability-cell incoming-availability-cell-${dayTone(date)}`}
        renderDayContents={(day, date) => {
          if (format(date, "yyyy-MM-dd") < today) return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} วันที่ผ่านมา`}><span className="incoming-availability-day-number">{day}</span></span>;
          if (!isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer)) return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} ไม่รับจอง`}><span className="incoming-availability-day-number">{day}</span><small>ไม่รับจอง</small></span>;
          const info = availability(format(date, "yyyy-MM-dd"));
          const label = !info ? (current && current.error ? "ไม่ทราบ" : "…") : !info.total ? "ไม่มีรอบ" : !info.available ? "เต็ม" : `ว่าง ${info.available} ที่`;
          return <span className="incoming-availability-day" aria-label={`${format(date, "dd/MM/yyyy")} ${label}`}>
            <span className="incoming-availability-day-number">{day}</span><small>{label}</small>
          </span>;
        }}
      />}
      <div className={enabled ? "incoming-availability-selection" : undefined}>
      {enabled && <label htmlFor={props.id} className="font-weight-bold">วันที่เลือก <span className="small font-weight-normal text-muted">(dd/mm/yyyy)</span></label>}
      <IncomingDateInput
        {...props}
        minDate={enabled ? parseIncomingDate(today) : undefined}
        filterDate={enabled ? (date) => isIncomingBookingDayAllowed(format(date, "yyyy-MM-dd"), customer) : undefined}
        disabled={props.disabled || locked}
        defaultValue={defaultValue}
        value={selected}
        onChange={chooseDate}
        open={enabled ? false : undefined}
        onCalendarOpen={() => {
          setMonth(monthKey(calendarDate(selected)));
          setRefresh((value) => value + 1);
        }}
        onMonthChange={(date) => setMonth(monthKey(date))}
        onYearChange={(date) => setMonth(monthKey(date))}
      />
      {enabled && <div className="incoming-availability-summary" aria-live="polite">
        {bookingPath && locked && <p className="text-warning font-weight-bold">
          {linked && linked.error ? "ตรวจสอบ Booking ที่เชื่อมโยงไม่สำเร็จ กรุณาอัปเดตห้องว่าง" : "กำลังตรวจสอบ Booking ที่เชื่อมโยง…"}
        </p>}
        {linked && linked.missing && <p className="text-warning">ไม่พบรายการจองเดิม ระบบจะตรวจสอบ Booking ล่าสุดของผู้ป่วยเมื่อบันทึก</p>}
        {checking ? <div role="status">กำลังตรวจสอบห้องว่าง…</div> : failed ?
          <div className="text-danger">ตรวจสอบห้องว่างไม่สำเร็จ <button type="button" className="btn btn-link btn-sm p-0" disabled={props.disabled} onClick={refreshAvailability}>ลองอีกครั้ง</button></div> :
          selected && selected < today ? <div className="text-muted">{formatIncomingDate(selected)} · วันที่ผ่านมา</div> :
          selected && !isIncomingBookingDayAllowed(selected, customer) ? <div className="text-warning">CHG11 รับจองเฉพาะวันพฤหัสบดี ศุกร์ และเสาร์</div> :
          selected && selectedDay ? <div className={`incoming-selection-status ${selectedDay.available ? "incoming-slot-open" : "incoming-slot-closed"}`}>
            {formatIncomingDate(selected)} · {selectedDay.available ? `ว่าง ${selectedDay.available} ที่` : "ไม่ว่าง"}
            {selectedDay.total === 0 && " (ยังไม่มีรายการจองห้อง)"}
          </div> : <div className="text-muted">เลือกวันที่ในปฏิทิน หรือพิมพ์วันที่ด้านบน</div>}
        <p className="small text-muted mt-3 mb-0">ระบบจะตรวจสอบห้องว่างอีกครั้งก่อนบันทึก</p>
        <p className="small text-muted mt-1 mb-0">เมื่อบันทึก ระบบจะตรวจสอบ Booking ล่าสุดก่อนใช้รอบจองที่ว่าง เปลี่ยนวันที่ได้เฉพาะ Sleep Test Scheduled</p>
      </div>}
      </div>
      </div>
    </div>
  );
});

export default IncomingStudyDateInput;
